import {Label, Search, useAnimatedPopper, type FormControl} from 'platform/components';
import {Box, Clickable, getSize, Integer, Text, VStack} from 'platform/foundation';
import {useLocale} from 'platform/locale';
import styled from 'styled-components';

import {KeyboardEvent, useCallback, useEffect, useRef, useState} from 'react';
import {useController} from 'react-hook-form';

import {isNil, isNotNil, juxt} from 'ramda';
import {floor, isNotNilOrEmpty, isObject, noop} from 'ramda-adjunct';

import {ApiError, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {usePriceReport} from '../../../hooks/usePriceReport';
import i18n from '../../../i18n/i18n';
import {useAddrSuggestionsQuery, useLazyAddrDetailQuery} from '../../../store/addressApi';
import {AddrSuggestionsApiResponse} from '../../../types/api/api';
import {FilterFormAddress, FilterFormData} from '../../../types/FilterFormData';
import {handleApiError} from '../../../utils/handleApiError';
import {useHandleKeyDownEvent} from '../hooks/useHandleKeydownEvent';
import {parseAddress} from '../utils/parseAddress';

export interface AddressSearchProps extends RequiredTestIdProps {
  name: keyof FilterFormData;
  control: FormControl<FilterFormData>;
}

export function AddressSearch(props: AddressSearchProps) {
  const {language} = useLocale();
  const {priceReport} = usePriceReport();
  const {
    field: {value, onChange: fieldOnChange},
  } = useController(props);
  const controlOnChange = props.control.onChange;

  // Use the same pattern as platform FormField components
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChange = juxt<any[], unknown>([fieldOnChange, controlOnChange ?? noop]);

  const widthRef = useRef<HTMLDivElement>(null);

  const [getAddressDetail] = useLazyAddrDetailQuery();

  const addressValue =
    isObject(value) && 'label' in (value as object) ? (value as FilterFormAddress) : null;
  const defaultLabel = addressValue?.label || null;
  const hasAddressCoordinates = isNotNil(addressValue?.lat) && isNotNil(addressValue?.lng);
  const priceReportAddress = priceReport?.vehicle.location?.address ?? null;
  const [searchText, setSearchText] = useState<string | null>(defaultLabel);
  const [isOptSelected, setIsOptSelected] = useState<boolean>(hasAddressCoordinates);
  const isDefaultAddressLookup =
    isNotNil(priceReportAddress) &&
    addressValue?.label === priceReportAddress &&
    !hasAddressCoordinates &&
    searchText === defaultLabel;
  const isAddressCommitted = hasAddressCoordinates && isOptSelected && searchText === defaultLabel;

  // Default address coordinates are resolved by useLookupDefaultAddress. While that
  // happens, this field should only display the label and must not open its own suggestions.
  const {data: suggestions} = useAddrSuggestionsQuery(
    {
      term: searchText,
      country: priceReport!.market,
      language,
    },
    {
      skip: isNil(searchText) || isNil(priceReport) || isDefaultAddressLookup || isAddressCommitted,
    }
  );
  const {Popper, closePopper, openPopper, referenceRef, popperProps, isOpen} = useAnimatedPopper({
    placement: 'bottom-start',
  });

  const suggestionOptions = suggestions && suggestionsToOptions(suggestions);

  const handleSubmitAddress = useCallback(
    (placeId: string, label?: string) => {
      getAddressDetail({placeId, language})
        .unwrap()
        .then((addressResponse) => {
          if (isObject(addressResponse)) {
            const address: FilterFormAddress = {
              label: label ?? parseAddress(addressResponse) ?? '',
              lng: isNotNil(addressResponse.gps) ? parseFloat(addressResponse.gps.lng) : 0,
              lat: isNotNil(addressResponse.gps) ? parseFloat(addressResponse.gps.lat) : 0,
            };
            onChange(address);
          }
          setSearchText(label ?? parseAddress(addressResponse));
        })
        .then(() => setIsOptSelected(true))
        .then(closePopper)
        .catch((error: ApiError) => {
          handleApiError(error);
        });
    },
    [getAddressDetail, language, closePopper, onChange]
  );

  const handleClearAddress = useCallback(() => {
    onChange(null);
  }, [onChange]);

  const handleSearchTextChange = useCallback(
    (text: string | null) => {
      setSearchText(text);

      if (hasAddressCoordinates && text !== defaultLabel) {
        setIsOptSelected(false);
        onChange(isNotNilOrEmpty(text) ? {label: text} : null);
      }
    },
    [defaultLabel, hasAddressCoordinates, onChange]
  );

  const [focusedOptionIndex] = useHandleKeyDownEvent({
    handleSubmitAddress,
    isOpen,
    suggestions: suggestionOptions,
  });

  useEffect(
    function syncSearchTextWithValue() {
      // The form value can change programmatically on load/reset after the default
      // address lookup resolves, so keep the visible input in sync with that value.
      if (isNil(value)) {
        setSearchText(null);
        setIsOptSelected(false);
        closePopper();
        return;
      }

      setSearchText(defaultLabel);

      if (hasAddressCoordinates) {
        setIsOptSelected(true);
        closePopper();
      }
    },
    [value, defaultLabel, hasAddressCoordinates, closePopper]
  );

  useEffect(
    function openSuggestionsPopper() {
      // Resolved or explicitly selected addresses should stay closed. Only user-entered
      // search text should auto-open the suggestion popper.
      if (isAddressCommitted) {
        closePopper();
        return;
      }

      if (isNotNilOrEmpty(searchText) && searchText !== defaultLabel) {
        openPopper();
      }
    },
    [searchText, openPopper, isAddressCommitted, defaultLabel, closePopper]
  );

  return (
    <>
      <Box ref={widthRef}>
        <VStack>
          <Label>{i18n.t('entity.filterForm.labels.address')}</Label>
          <div
            ref={referenceRef}
            onClick={openPopper}
            onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
              e.key === 'Enter' && e.preventDefault();
            }}
          >
            <Search
              value={searchText}
              icon="maps/place"
              placeholder={i18n.t('entity.filterForm.placeholders.address')}
              onFocus={() => {
                openPopper();
                setIsOptSelected(false);
              }}
              onClear={() => {
                closePopper();
                handleClearAddress();
              }}
              onChange={handleSearchTextChange}
              data-testid={suffixTestId('address-search', props)}
            />
          </div>
        </VStack>
      </Box>

      <Popper
        {...popperProps}
        isOpen={isOpen && isNotNilOrEmpty(suggestionOptions) && isNotNilOrEmpty(searchText)}
      >
        <Box
          paddingTop={2}
          width={(floor((widthRef.current?.clientWidth ?? 0) / 4) as Integer) ?? '100%'}
        >
          <Box
            backgroundColor="general.white"
            borderRadius="small"
            boxShadow="elevation_2"
            padding={2}
          >
            <VStack>
              {suggestionOptions?.map((item, index) => (
                <Clickable
                  key={item.value}
                  onClick={() => {
                    setIsOptSelected(true);
                    handleSubmitAddress(item.value, item.label);
                  }}
                  data-testid={suffixTestId(`address-option-${index + 1}`, props)}
                >
                  <BoxWithHover isFocused={focusedOptionIndex === index}>
                    <Text size="small">{item.label}</Text>
                  </BoxWithHover>
                </Clickable>
              ))}
            </VStack>
          </Box>
        </Box>
      </Popper>
    </>
  );
}

const BoxWithHover = styled.div<{isFocused: boolean}>`
  padding: ${getSize(2)};
  border-radius: ${({theme}) => theme.radii.xSmall};
  ${({isFocused, theme}) =>
    isFocused ? ` background-color: ${theme.colors.palettes.blue[10][100]};` : ``}

  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.blue[20][100]};
  }
`;

const suggestionsToOptions = (val: AddrSuggestionsApiResponse) =>
  val.map((item) => ({
    value: item?.placeId ?? '',
    label: item?.formattedAddress ?? i18n.t('entity.filterForm.labels.noAddresses'),
  }));
